import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO } from "@/data/content";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "Events", id: "events" },
  { label: "Venue Spaces", id: "venue-spaces" },
  { label: "Gallery", id: "gallery" },
  { label: "Tickets", route: "/tickets" },
  { label: "Book Now", id: "booking" },
  { label: "Contact", id: "contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleLink = (l) => {
    if (l.route) {
      setOpen(false);
      navigate(l.route);
    } else {
      go(l.id);
    }
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled ? "bg-epic-black/85 backdrop-blur-2xl border-b border-white/5 py-4" : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between">
        <button data-testid="nav-logo" onClick={() => go("home")} className="flex items-center gap-3 leading-none">
          <img src={LOGO} alt="The Epic Center logo" className="h-14 md:h-16 w-auto object-contain" />
          <span className="flex flex-col items-start">
            <span className="font-display text-2xl md:text-3xl text-white tracking-wide">THE EPIC CENTER</span>
            <span className="text-[0.6rem] md:text-xs tracking-[0.4em] text-epic-gold uppercase">Birmingham</span>
          </span>
        </button>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${(l.id || l.route.replace("/", "")).toLowerCase()}`}
              onClick={() => handleLink(l)}
              className="text-sm uppercase tracking-[0.15em] text-white/80 hover:text-epic-gold transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-book-cta"
            onClick={() => go("booking")}
            className="bg-epic-gold text-epic-black font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-epic-goldhover transition-colors duration-300"
          >
            Book The Venue
          </button>
        </div>

        <button data-testid="nav-mobile-toggle" className="lg:hidden text-white" onClick={() => setOpen(!open)}>
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-epic-black/95 backdrop-blur-2xl border-t border-white/5 mt-4">
          <div className="flex flex-col px-6 py-6 gap-5">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`mobile-link-${(l.id || l.route.replace("/", "")).toLowerCase()}`}
                onClick={() => handleLink(l)}
                className="text-left text-base uppercase tracking-[0.15em] text-white/80 hover:text-epic-gold"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
